import { locateRow } from "../find";
import { blockIdsInDragItem } from "../selection/block-bulk-ops";
import { rowIdsInDragItem } from "../selection/row-bulk-ops";
import type { DragData, DropData } from "./types";
import type { EmailDocument, BlockType } from "../types";
import type { RowInsertTarget } from "../mutations/index";
import type { ColumnPresetId } from "../column-presets";
import type { SnippetId } from "../snippets";

export type DropActions = {
  getDocument: () => EmailDocument;
  insertBlock: (
    rowId: string,
    columnId: string,
    index: number,
    blockType: BlockType,
  ) => void;
  moveBlock: (
    from: {
      rowId: string;
      columnId: string;
      blockId: string;
      blockIds?: string[];
    },
    to: { rowId: string; columnId: string; index: number },
  ) => void;
  insertRowWithBlock: (target: RowInsertTarget, blockType: BlockType) => void;
  moveBlockToNewRow: (
    from: { rowId: string; columnId: string; blockId: string },
    target: RowInsertTarget,
  ) => void;
  insertRow: (target: RowInsertTarget, presetId: ColumnPresetId) => void;
  insertGroup: (index: number) => void;
  insertSnippet: (snippetId: SnippetId, index: number) => void;
  moveTopLevelSection: (fromIndex: number, toIndex: number) => void;
  moveContiguousTopLevelRows: (rowIds: string[], slotIndex: number) => void;
  moveContiguousGroupRows: (
    groupId: string,
    rowIds: string[],
    slotIndex: number,
  ) => void;
  moveRowInGroup: (groupId: string, fromIndex: number, toIndex: number) => void;
  moveRowBetweenParents: (
    rowId: string,
    to:
      | { parent: 'document'; index: number }
      | { parent: 'group'; groupId: string; rowIndex: number },
  ) => void;
};

function rowInsertTargetFromDrop(
  over: Extract<DropData, { kind: 'section-slot' | 'group-row-slot' }>,
): RowInsertTarget {
  if (over.kind === 'section-slot') {
    return over.index;
  }
  return { groupId: over.groupId, rowIndex: over.index };
}

export function applyDrop(
  active: DragData,
  over: DropData,
  actions: DropActions,
): void {
  if (active.kind === 'palette-block' && over.kind === 'block-slot') {
    actions.insertBlock(
      over.rowId,
      over.columnId,
      over.index,
      active.blockType,
    );
    return;
  }
  if (active.kind === 'item-block' && over.kind === 'block-slot') {
    const blockIds = blockIdsInDragItem(active);
    actions.moveBlock(
      {
        rowId: active.rowId,
        columnId: active.columnId,
        blockId: active.blockId,
        blockIds: blockIds.length > 1 ? blockIds : undefined,
      },
      { rowId: over.rowId, columnId: over.columnId, index: over.index },
    );
    return;
  }
  if (
    active.kind === 'palette-block' &&
    (over.kind === 'section-slot' || over.kind === 'group-row-slot')
  ) {
    actions.insertRowWithBlock(rowInsertTargetFromDrop(over), active.blockType);
    return;
  }
  if (
    active.kind === 'item-block' &&
    (over.kind === 'section-slot' || over.kind === 'group-row-slot')
  ) {
    actions.moveBlockToNewRow(
      {
        rowId: active.rowId,
        columnId: active.columnId,
        blockId: active.blockId,
      },
      rowInsertTargetFromDrop(over),
    );
    return;
  }
  if (active.kind === 'palette-row' && over.kind === 'section-slot') {
    actions.insertRow(over.index, active.presetId);
    return;
  }
  if (active.kind === 'palette-row' && over.kind === 'group-row-slot') {
    actions.insertRow(
      { groupId: over.groupId, rowIndex: over.index },
      active.presetId,
    );
    return;
  }
  if (active.kind === 'palette-group' && over.kind === 'section-slot') {
    actions.insertGroup(over.index);
    return;
  }
  if (active.kind === 'palette-section' && over.kind === 'section-slot') {
    actions.insertSnippet(active.snippetId, over.index);
    return;
  }
  if (active.kind === 'item-group' && over.kind === 'section-slot') {
    const sections = actions.getDocument().sections;
    const fromIndex = sections.findIndex(
      section => section.id === active.groupId,
    );
    if (fromIndex >= 0) {
      actions.moveTopLevelSection(fromIndex, over.index);
    }
    return;
  }
  if (active.kind === 'item-row' && over.kind === 'section-slot') {
    const sections = actions.getDocument().sections;
    const rowIds = rowIdsInDragItem(active);
    if (rowIds.length > 1) {
      actions.moveContiguousTopLevelRows(rowIds, over.index);
      return;
    }
    const located = locateRow(sections, active.rowId);
    if (!located) {
      return;
    }
    if (located.location.parent === 'document') {
      actions.moveTopLevelSection(located.location.sectionIndex, over.index);
    } else {
      actions.moveRowBetweenParents(active.rowId, {
        parent: 'document',
        index: over.index,
      });
    }
    return;
  }
  if (active.kind === 'item-row' && over.kind === 'group-row-slot') {
    const sections = actions.getDocument().sections;
    const rowIds = rowIdsInDragItem(active);
    if (rowIds.length > 1) {
      const located = locateRow(sections, active.rowId);
      if (
        located?.location.parent === 'group' &&
        located.location.groupId === over.groupId
      ) {
        actions.moveContiguousGroupRows(over.groupId, rowIds, over.index);
      }
      return;
    }
    const located = locateRow(sections, active.rowId);
    if (!located) {
      return;
    }
    if (
      located.location.parent === 'group' &&
      located.location.groupId === over.groupId
    ) {
      actions.moveRowInGroup(
        over.groupId,
        located.location.rowIndex,
        over.index,
      );
    } else {
      actions.moveRowBetweenParents(active.rowId, {
        parent: 'group',
        groupId: over.groupId,
        rowIndex: over.index,
      });
    }
  }
}
